import React from 'react';
import { Filter, Search, X } from 'lucide-react';
import type { ContactFilters, ContactType, ContactStatus, LeadSegment, TeamMember } from '../../types';
import { CONTACT_TYPES, CONTACT_STATUSES, FOLLOW_UP_FILTERS, LEAD_SEGMENTS } from '../../constants';

interface LeadFiltersProps {
  filters: ContactFilters;
  onChange: (filters: ContactFilters) => void;
  members: TeamMember[];
}

const LeadFilters: React.FC<LeadFiltersProps> = ({ filters, onChange, members }) => {
  const hasActiveFilters = filters.owner || filters.type || filters.status || filters.followUp || filters.segment || filters.needsEmail || filters.query;

  const clearFilters = () => {
    onChange({ owner: '', type: '', status: '', followUp: '', segment: '', needsEmail: false, query: '' });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <Filter size={18} className="text-slate-400" />

      <div className="relative min-w-[220px] flex-1">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="search"
          value={filters.query || ''}
          onChange={(e) => onChange({ ...filters, query: e.target.value })}
          placeholder="Search name, company, email…"
          className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      <select
        value={filters.owner || ''}
        onChange={(e) => onChange({ ...filters, owner: e.target.value })}
        className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
      >
        <option value="">All Owners</option>
        {members.map((m) => (
          <option key={m.user_id} value={m.user_id}>
            {m.email || m.user_id.slice(0, 8)}
          </option>
        ))}
      </select>

      <select
        value={filters.type || ''}
        onChange={(e) => onChange({ ...filters, type: e.target.value as ContactType | '' })}
        className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
      >
        <option value="">All Types</option>
        {CONTACT_TYPES.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>

      <select
        value={filters.segment || ''}
        onChange={(e) => onChange({ ...filters, segment: e.target.value as LeadSegment | '' })}
        className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
      >
        <option value="">All segments</option>
        {LEAD_SEGMENTS.map((s) => (
          <option key={s.value} value={s.value}>{s.label}</option>
        ))}
      </select>

      <select
        value={filters.status || ''}
        onChange={(e) => onChange({ ...filters, status: e.target.value as ContactStatus | '' })}
        className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
      >
        <option value="">All Statuses</option>
        {CONTACT_STATUSES.map((s) => (
          <option key={s.value} value={s.value}>{s.label}</option>
        ))}
      </select>

      <select
        value={filters.followUp || ''}
        onChange={(e) => onChange({ ...filters, followUp: e.target.value as ContactFilters['followUp'] })}
        className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
      >
        {FOLLOW_UP_FILTERS.map((f) => (
          <option key={f.value} value={f.value}>{f.label}</option>
        ))}
      </select>

      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input
          type="checkbox"
          checked={!!filters.needsEmail}
          onChange={(e) => onChange({ ...filters, needsEmail: e.target.checked })}
          className="rounded border-slate-600 bg-slate-800"
        />
        Needs email
      </label>

      {hasActiveFilters && (
        <button
          onClick={clearFilters}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-400 hover:text-white transition-colors"
        >
          <X size={14} />
          Clear
        </button>
      )}
    </div>
  );
};

export default LeadFilters;
